<template>
  <header class="cabecalho">
    <div class="conteudo cabecalho__interior">
      <a href="/" class="cabecalho__marca" aria-label="Ir para a página inicial">
        <MarcaLogo :tamanho="32" />
      </a>

      <div class="cabecalho__acoes">
        <span class="cabecalho__cidade">
          <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
            <path d="M12 21s7-6.3 7-11a7 7 0 1 0-14 0c0 4.7 7 11 7 11Z" />
            <circle cx="12" cy="10" r="2.6" />
          </svg>
          <span class="cabecalho__cidade-rotulo">São Bento, PB</span>
        </span>

        <!-- Desktop: acesso direto ao Portal ADM. -->
        <a :href="PORTAL_ADM" class="cabecalho__portal" target="_blank" rel="noopener noreferrer">
          <svg viewBox="0 0 24 24" width="17" height="17" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
            <circle cx="12" cy="8" r="3.6" />
            <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
          </svg>
          <span class="cabecalho__portal-rotulo">Portal ADM</span>
        </a>

        <!-- Mobile: um menu abre o mesmo acesso ao Portal ADM. -->
        <div ref="menu" class="cabecalho__menu" @keydown.esc="menuAberto = false">
          <button
            type="button"
            class="cabecalho__menu-gatilho"
            :aria-expanded="menuAberto"
            aria-haspopup="true"
            aria-label="Abrir menu"
            @click="menuAberto = !menuAberto"
          >
            <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" aria-hidden="true">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>

          <div v-if="menuAberto" class="cabecalho__menu-painel" role="menu">
            <a
              :href="PORTAL_ADM"
              class="cabecalho__menu-item"
              target="_blank"
              rel="noopener noreferrer"
              role="menuitem"
              @click="menuAberto = false"
            >
              <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" aria-hidden="true">
                <circle cx="12" cy="8" r="3.6" />
                <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
              </svg>
              Portal ADM
            </a>
          </div>
        </div>
      </div>

      <form class="busca" role="search" @submit.prevent>
        <AlternadorDeRecurso :recurso="recurso" @atualizar="emit('atualizar:recurso', $event)" />

        <label class="apenas-leitor-de-tela" for="campo-busca">{{ rotuloDaBusca }}</label>
        <input
          id="campo-busca"
          type="search"
          name="busca"
          autocomplete="off"
          spellcheck="false"
          maxlength="80"
          :value="termo"
          :placeholder="rotuloDaBusca"
          @input="emit('atualizar:termo', $event.target.value)"
        >

        <span class="busca__icone" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="19" height="19" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round">
            <circle cx="11" cy="11" r="7" />
            <path d="M20 20l-3.6-3.6" />
          </svg>
        </span>
      </form>
    </div>
  </header>
</template>

<script setup>
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
  import MarcaLogo from '@/components/marketplace/MarcaLogo.vue';
  import AlternadorDeRecurso from '@/components/marketplace/AlternadorDeRecurso.vue';

  const PORTAL_ADM = 'https://app.crescimentoconectado.com.br/admin_crescc';

  const props = defineProps({
    termo: {
      type: String,
      default: ''
    },
    recurso: {
      type: String,
      default: 'shops'
    }
  });

  const emit = defineEmits(['atualizar:termo', 'atualizar:recurso']);

  const rotuloDaBusca = computed(() => props.recurso === 'shops'
    ? 'Busque por loja'
    : 'Busque por produto');

  const menuAberto = ref(false);
  const menu = ref(null);

  /* Fecha o menu ao clicar fora dele — inclusive em qualquer ponto da barra. */
  function aoClicarFora(evento) {
    if (!menuAberto.value) return;

    if (menu.value && !menu.value.contains(evento.target)) {
      menuAberto.value = false;
    }
  }

  onMounted(() => document.addEventListener('click', aoClicarFora));
  onBeforeUnmount(() => document.removeEventListener('click', aoClicarFora));
</script>

<style scoped>
  .cabecalho {
    position: sticky;
    top: 0;
    z-index: 50;
    background: var(--cor-superficie);
    border-bottom: 1px solid var(--cor-linha);
  }

  .cabecalho__interior {
    display: grid;
    grid-template-columns: auto 1fr;
    align-items: center;
    gap: var(--espaco-3);
    padding-block: var(--espaco-3);
  }

  .cabecalho__marca {
    flex: none;
  }

  .cabecalho__acoes {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: var(--espaco-2);
  }

  /* Indicador fixo: a plataforma atende apenas Sao Bento, sem escolha de cidade. */
  .cabecalho__cidade {
    display: inline-flex;
    align-items: center;
    gap: var(--espaco-2);
    padding: var(--espaco-2);
    border-radius: var(--raio-pilula);
    background: var(--cor-marca-clara);
    color: var(--cor-marca-escura);
    font-size: 0.82rem;
    font-weight: 600;
    white-space: nowrap;
  }

  .cabecalho__cidade-rotulo {
    display: none;
  }

  /* No mobile o acesso ao Portal ADM vem pelo menu; o botao direto e do desktop. */
  .cabecalho__portal {
    display: none;
    align-items: center;
    gap: var(--espaco-2);
    padding: var(--espaco-2) var(--espaco-3);
    border-radius: var(--raio-pilula);
    background: var(--cor-marca);
    color: #fff;
    font-size: 0.82rem;
    font-weight: 700;
    white-space: nowrap;
    transition: background var(--transicao-padrao);
  }

  .cabecalho__portal:hover {
    background: var(--cor-marca-escura);
  }

  .cabecalho__portal-rotulo {
    display: none;
  }

  .cabecalho__menu {
    position: relative;
    display: inline-flex;
  }

  .cabecalho__menu-gatilho {
    display: grid;
    place-items: center;
    width: 40px;
    height: 40px;
    border-radius: var(--raio-pilula);
    background: var(--cor-marca);
    color: #fff;
    transition: background var(--transicao-padrao);
  }

  .cabecalho__menu-gatilho:hover {
    background: var(--cor-marca-escura);
  }

  .cabecalho__menu-painel {
    position: absolute;
    top: calc(100% + var(--espaco-2));
    right: 0;
    min-width: 190px;
    padding: var(--espaco-1);
    background: var(--cor-superficie);
    border: 1px solid var(--cor-linha);
    border-radius: var(--raio-medio);
    box-shadow: var(--sombra-forte);
    z-index: 60;
    animation: surgir-menu 0.16s var(--transicao-padrao) both;
  }

  .cabecalho__menu-item {
    display: flex;
    align-items: center;
    gap: var(--espaco-2);
    padding: var(--espaco-3);
    border-radius: var(--raio-pequeno);
    color: var(--cor-tinta);
    font-size: 0.9rem;
    font-weight: 600;
    transition: background var(--transicao-padrao), color var(--transicao-padrao);
  }

  .cabecalho__menu-item svg {
    color: var(--cor-marca);
  }

  .cabecalho__menu-item:hover {
    background: var(--cor-marca-clara);
    color: var(--cor-marca-escura);
  }

  @keyframes surgir-menu {
    from {
      opacity: 0;
      transform: translateY(-6px);
    }

    to {
      opacity: 1;
      transform: none;
    }
  }

  /*
   * No prototipo a borda ficava no proprio input. Aqui ela envolve o campo
   * inteiro porque o alternador produtos/lojas vive dentro da mesma pilula,
   * preservando o fluxo de busca que a plataforma ja oferece hoje.
   */
  .busca {
    grid-column: 1 / -1;
    position: relative;
    display: flex;
    align-items: center;
    gap: var(--espaco-2);
    padding: var(--espaco-1) var(--espaco-4) var(--espaco-1) var(--espaco-1);
    border: 1.6px solid var(--cor-marca);
    border-radius: var(--raio-pilula);
    background: var(--cor-superficie);
  }

  .busca:focus-within {
    box-shadow: 0 0 0 4px var(--cor-marca-clara);
  }

  .busca input {
    flex: 1;
    min-width: 0;
    padding: var(--espaco-2) 0;
    border: 0;
    outline: none;
    font-size: 16px;
    background: transparent;
    color: var(--cor-texto);
  }

  .busca input::placeholder {
    color: var(--cor-texto-tenue);
  }

  .busca__icone {
    flex: none;
    display: grid;
    place-items: center;
    color: var(--cor-marca);
    pointer-events: none;
  }

  @media (min-width: 640px) {
    .cabecalho__portal-rotulo,
    .cabecalho__cidade-rotulo {
      display: inline;
    }

    .cabecalho__cidade {
      padding: var(--espaco-2) var(--espaco-4);
    }

    /* No desktop volta o botao direto e o menu mobile some. */
    .cabecalho__portal {
      display: inline-flex;
      padding: var(--espaco-2) var(--espaco-5);
    }

    .cabecalho__menu {
      display: none;
    }
  }

  @media (min-width: 1024px) {
    .cabecalho__interior {
      grid-template-columns: auto minmax(0, 1fr) auto;
      height: var(--altura-cabecalho);
      padding-block: 0;
      gap: var(--espaco-4);
    }

    .busca {
      grid-column: 2;
      grid-row: 1;
      max-width: 520px;
    }

    .cabecalho__acoes {
      grid-column: 3;
      grid-row: 1;
    }
  }
</style>
